import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { Search, Tag, Loader2, CheckCircle2, Banknote, CreditCard, Smartphone, User, FileText, X, Printer, Trash2, AlertCircle, Send } from 'lucide-react';
import { printerService, type PrinterStatus } from '../../lib/print/printerService';
import { CustomerSearch } from './CustomerSearch';
import { PrinterDrawer } from './PrinterDrawer';
import { ReceiptPreview, printReceipt, type ReceiptData } from './Receipt';
import { DEFAULT_RECEIPT_CONFIGURATION, loadReceiptConfiguration } from '../../lib/receiptConfig';
import { posApi, offersApi, accountingApi, apiClient, API_ENDPOINTS, type CustomerResponse, type ProductSearchResult, type DiscountRule, type OrderLineCreateRequest } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import type { AccountingAccount } from '../../lib/accounting';
// Removed useMerchantSSE as we now listen to global custom events
import { confirmAction } from '../modals/ConfirmModal';


interface CartLine {
  product: ProductSearchResult;
  qty: number;
  discount_amount: number;
  unit_price: number;
  service_charge: number;
  applied_auto_discount?: any;
}

function toOrderLineRequest(line: CartLine): OrderLineCreateRequest {
  return {
    product_id: line.product.id.startsWith('service-') ? null : line.product.id,
    product_name: line.product.name,
    qty: line.qty,
    unit_price: line.unit_price,
    discount_total: line.discount_amount * line.qty,
    service_charge: line.service_charge || 0,
  };
}

function mapAccountKind(accountType: string): AccountingAccount['kind'] {
  if (accountType === 'asset') return 'cash';
  if (accountType === 'liability') return 'credit';
  if (accountType === 'revenue') return 'mobile_money';
  if (accountType === 'equity') return 'bank';
  return 'other';
}

function mapAccountingAccount(account: {
  id: string;
  name: string;
  code: string;
  account_type: string;
  opening_balance: string;
  balance: string;
  is_system: boolean;
  is_active: boolean;
  created_at: string;
}): AccountingAccount {
  return {
    id: account.id,
    name: account.name,
    payment_method: account.code,
    kind: mapAccountKind(account.account_type),
    opening_balance: Number(account.opening_balance) || 0,
    balance: Number(account.balance) || 0,
    is_system: account.is_system,
    is_active: account.is_active,
    created_at: account.created_at,
  };
}

function Keypad({ onInput, mode, setMode, canApplyDiscount = true, canOverridePrice = true }: { onInput: (val: string) => void, mode: 'qty' | 'disc' | 'price' | 'service', setMode: (m: 'qty' | 'disc' | 'price' | 'service') => void, canApplyDiscount?: boolean, canOverridePrice?: boolean }) {
  const mainKeys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '+/-', '0', '.'];
  const sideKeys = [
    { label: 'Qty', value: 'qty' },
    ...(canApplyDiscount ? [{ label: '% Disc', value: 'disc' }] : []),
    ...(canOverridePrice ? [{ label: 'Price', value: 'price' }] : []),
    { label: 'Service', value: 'service' },
    { label: '⌫', value: 'backspace' }
  ];
  
  return (
    <div style={{ padding: '4px', background: '#e5e7eb', borderTop: '1px solid #d1d5db' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '3fr 1fr', gap: '4px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '4px' }}>
          {mainKeys.map(key => (
            <button
              key={key}
              type="button"
              onClick={() => onInput(key)}
              style={{
                height: '46px',
                fontSize: '16px',
                fontWeight: 600,
                background: key === '+/-' ? '#fef3c7' : 'white',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                cursor: 'pointer',
                color: '#111827'
              }}
            >
              {key}
            </button>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '4px' }}>
          {sideKeys.map((k) => (
            <button
              key={k.label}
              type="button"
              onClick={() => k.value === 'backspace' ? onInput('backspace') : setMode(k.value as any)}
              style={{
                height: '35px',
                fontSize: '12px',
                fontWeight: 600,
                background: k.value === 'backspace' ? '#fecaca' : (mode === k.value ? '#d1fae5' : '#f3f4f6'),
                border: mode === k.value ? '2px solid #064734' : '1px solid #d1d5db',
                borderRadius: '4px',
                cursor: 'pointer',
                color: '#111827'
              }}
            >
              {k.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function ProductGrid({ onAdd, autoDiscounts, searchQuery, storeId }: { onAdd: (p: ProductSearchResult) => void, autoDiscounts: DiscountRule[], searchQuery?: string, storeId?: string }) {
  const { activeStore } = useAuth();
  const targetStoreId = storeId || activeStore?.id;
  const allowNegativeStock = Boolean(activeStore?.config?.allow_negative_stock || true);
  const [category, setCategory] = useState<string | null>(null);

  const { data: productsData, isLoading: loadingProducts } = useQuery({
    queryKey: ['pos-products', targetStoreId],
    queryFn: async () => {
      if (!targetStoreId) return { items: [] };
      return posApi.listProducts({ limit: 200, storeId: targetStoreId });
    },
    enabled: !!targetStoreId,
    staleTime: 5 * 60 * 1000,
  });

  const { data: categoriesData, isLoading: loadingCategories } = useQuery({
    queryKey: ['pos-categories', targetStoreId],
    queryFn: async () => {
      const res = await apiClient.get<any>(API_ENDPOINTS.PRODUCTS.CATEGORIES);
      return Array.isArray(res) ? res : res?.data || [];
    },
    enabled: !!targetStoreId,
    staleTime: 5 * 60 * 1000,
  });

  const products = productsData?.items || [];
  const storeCategories = categoriesData || [];
  const loading = loadingProducts || loadingCategories;

  const getInitials = (name: string) => {
    const letters = name.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    return letters.slice(0, 2) || 'P';
  };

  function ProductThumbnail({ name, imageUrl }: { name: string; imageUrl?: any }) {
    const [hasImageError, setHasImageError] = useState(false);

    let finalUrl: string | null = null;
    if (typeof imageUrl === 'string' && imageUrl) {
      finalUrl = imageUrl;
    } else if (imageUrl && typeof imageUrl === 'object') {
      finalUrl = imageUrl.url || imageUrl.src || null;
    } else if (Array.isArray(imageUrl) && imageUrl.length > 0) {
      const first = imageUrl[0];
      finalUrl = typeof first === 'string' ? first : (first?.url || first?.src || null);
    }

    if (finalUrl && finalUrl.startsWith('/') && !finalUrl.startsWith('//')) {
      try {
        const apiBase = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';
        if (apiBase.startsWith('http://') || apiBase.startsWith('https://')) {
          const origin = new URL(apiBase).origin;
          finalUrl = `${origin}${finalUrl}`;
        }
      } catch {}
    }

    useEffect(() => {
      setHasImageError(false);
    }, [finalUrl]);

    if (finalUrl && !hasImageError) {
      return (
        <img
          src={finalUrl}
          alt={name}
          style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }}
          onError={() => setHasImageError(true)}
        />
      );
    }

    return <span>{getInitials(name)}</span>;
  }

  const loadedCategories = products.map(p => p.category).filter(Boolean);
  const fetchedCategoryNames = storeCategories.map((c: any) => c.name).filter(Boolean);
  const categories = Array.from(new Set([...loadedCategories, ...fetchedCategoryNames])) as string[];
  const filtered = products.filter(p => {
    if (category && p.category !== category) return false;
    if (searchQuery && searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      return (
        (p.name && p.name.toLowerCase().includes(q)) ||
        (p.sku && p.sku.toLowerCase().includes(q)) ||
        (p.barcode && p.barcode.toLowerCase().includes(q))
      );
    }
    return true;
  });

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#9ca3af' }}>
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {categories.length > 0 && (
        <div style={{ display: 'flex', gap: '8px', padding: '12px 16px', overflowX: 'auto', background: 'white', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
          <button 
            type="button"
            onClick={() => setCategory(null)}
            style={{ 
              padding: '6px 14px', 
              border: `1px solid ${category === null ? '#064734' : '#d1d5db'}`, 
              background: category === null ? '#064734' : 'white',
              color: category === null ? 'white' : '#374151',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s',
              borderRadius: '6px'
            }}
          >
            All Items
          </button>
          {categories.map(cat => (
            <button 
              key={cat}
              type="button"
              onClick={() => setCategory(cat)}
              style={{ 
                padding: '6px 14px', 
                border: `1px solid ${category === cat ? '#064734' : '#d1d5db'}`, 
                background: category === cat ? '#064734' : 'white',
                color: category === cat ? 'white' : '#374151',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'all 0.2s',
                borderRadius: '6px'
              }}
            >
              {cat}
            </button>
          ))}
        </div>
      )}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(145px, 1fr))', gap: '14px', alignContent: 'start' }}>
        {filtered.map(p => {
          const isOutOfStock = p.track_stock && p.qty_on_hand !== undefined && p.qty_on_hand !== null && p.qty_on_hand <= 0;
          const isDisabled = isOutOfStock && !allowNegativeStock;

          return (
          <button 
            key={p.id}
            type="button"
            onClick={() => {
              if (!isDisabled) onAdd(p);
            }}
            disabled={isDisabled}
            style={{ 
              background: 'white', 
              border: '1px solid #e5e7eb', 
              borderRadius: '10px', 
              padding: '12px', 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'flex-start',
              textAlign: 'left',
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              opacity: isDisabled ? 0.5 : 1,
              transition: 'all 0.15s ease',
              gap: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              height: '160px',
              justifyContent: 'space-between'
            }}
          >
            <div style={{ 
              width: '100%', 
              height: '84px', 
              background: '#f8fafc', 
              borderRadius: '8px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              fontSize: '22px',
              color: '#94a3b8',
              fontWeight: 700,
              flexShrink: 0,
              overflow: 'hidden'
            }}>
              <ProductThumbnail name={p.name} imageUrl={p.image_url || (p as any).images?.[0] || (p as any).image || (p as any).images} />
            </div>
            <div style={{ flex: 1, width: '100%' }}>
              {isDisabled && (
                <div style={{
                  fontSize: '10px',
                  background: '#fee2e2',
                  color: '#991b1b',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  display: 'inline-block',
                  marginBottom: '4px',
                  fontWeight: 600
                }}>
                  Out of Stock
                </div>
              )}
              {autoDiscounts.some(r => 
                (r.applies_to === 'product' && r.product_ids?.includes(p.id)) ||
                (r.applies_to === 'category' && r.category_ids?.includes(p.category ?? ''))
              ) && (
                <div style={{
                  fontSize: '10px',
                  background: '#fef3c7',
                  color: '#92400e',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  display: 'inline-block',
                  marginBottom: '4px',
                  fontWeight: 600
                }}>
                  <Tag size={10} style={{ display: 'inline', marginRight: '2px', verticalAlign: 'text-top' }} />
                  Offer
                </div>
              )}
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#111827', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: '1.3' }}>
                {p.name}
              </div>
              <div style={{ fontSize: '12px', color: '#064734', fontWeight: 700, marginTop: '4px' }}>
                KSh {parseFloat(p.base_price).toLocaleString()}
              </div>
            </div>
          </button>
        )})}
      </div>
    </div>
  );
}

export interface RegisterActiveProps {
  sessionId: string;
  storeId?: string;
  locationId?: string;
  onClose: () => void;
}

export function RegisterActive({ sessionId, storeId, locationId, onClose }: RegisterActiveProps) {
  const { user, activeStore } = useAuth();
  const currentStoreId = storeId || locationId || activeStore?.id || '';
  const canApplyDiscount = user?.role === 'tenant_owner' || user?.access?.posPermissions?.canApplyDiscount;
  const canOverridePrice = user?.role === 'tenant_owner' || user?.access?.posPermissions?.canOverridePrice;
  const [search, setSearch] = useState('');
  const [activeOrders, setActiveOrders] = useState<any[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [selectedLineId, setSelectedLineId] = useState<string | null>(null);
  const [keypadMode, setKeypadMode] = useState<'qty' | 'disc' | 'price' | 'service'>('qty');
  const isNewInputRef = useRef(true);

  useEffect(() => {
    isNewInputRef.current = true;
  }, [keypadMode, selectedLineId]);

  const [charging, setCharging] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<string>('cash');
  const [paymentAccounts, setPaymentAccounts] = useState<AccountingAccount[]>([]);
  const [amountInput, setAmountInput] = useState<string>('');
  const [showCashTenderModal, setShowCashTenderModal] = useState(false);
  const [promoCodeInput, setPromoCodeInput] = useState('');
  const [appliedCouponCode, setAppliedCouponCode] = useState<string | null>(null);
  const [appliedCouponDiscount, setAppliedCouponDiscount] = useState(0);
  const [couponStatus, setCouponStatus] = useState<'idle' | 'validating' | 'valid' | 'invalid'>('idle');
  const [couponMessage, setCouponMessage] = useState<string | null>(null);
  const [customer, setCustomer] = useState<CustomerResponse | null>(null);
  const [showCustomerSearch, setShowCustomerSearch] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [orderNotesInput, setOrderNotesInput] = useState('');
  const [receiptConfig, setReceiptConfig] = useState<any>(DEFAULT_RECEIPT_CONFIGURATION);
  const [printerStatus, setPrinterStatus] = useState<PrinterStatus>(() => printerService.getStatus());
  const [showPrinterDrawer, setShowPrinterDrawer] = useState(false);
  const [autoDiscounts, setAutoDiscounts] = useState<DiscountRule[]>([]);
  const [stkPhone, setStkPhone] = useState<string>('');
  const [stkSending, setStkSending] = useState<boolean>(false);
  const [stkStatus, setStkStatus] = useState<'idle' | 'sent' | 'success' | 'error'>('idle');
  const [stkMessage, setStkMessage] = useState<string>('');
  const [stkReference, setStkReference] = useState<string>('');
  const [paidTransactionDetails, setPaidTransactionDetails] = useState<any>(null);
  const [paymentChannels, setPaymentChannels] = useState<any[]>([]);
  const [loadingChannels, setLoadingChannels] = useState<boolean>(false);
  const syncDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const draftSyncEnabledRef = useRef(true);
  const creatingOrderPromiseRef = useRef<Promise<any> | null>(null);

  const currentOrder = activeOrders[activeIndex] || { localLines: [], id: null };
  const cart = currentOrder.localLines || [];

  const activeStoreObj = user?.stores?.find((s: any) => s.id === currentStoreId) || activeStore;
  const vatMode = activeStoreObj?.vat_config?.mode ?? 'disabled';
  const vatRate = activeStoreObj?.vat_config?.rate || 16.0;
  const cartSubtotal = cart.reduce((sum: number, line: any) => sum + (line.qty * (line.unit_price - line.discount_amount)) + (line.service_charge || 0), 0);

  const cartTax = (() => {
    if (vatMode === 'added') {
      return cart.reduce((sum: number, line: any) => {
        const taxable = (line.qty * (line.unit_price - line.discount_amount)) + (line.service_charge || 0);
        const rate = line.product.tax_rate ? parseFloat(line.product.tax_rate) : (vatRate / 100);
        return sum + taxable * rate;
      }, 0);
    } else if (vatMode === 'included') {
      return cart.reduce((sum: number, line: any) => {
        const taxable = (line.qty * (line.unit_price - line.discount_amount)) + (line.service_charge || 0);
        const rate = line.product.tax_rate ? parseFloat(line.product.tax_rate) : (vatRate / 100);
        return rate > 0 ? sum + (taxable - taxable / (1 + rate)) : sum;
      }, 0);
    }
    return 0;
  })();

  const cartTotal = vatMode === 'added' ? cartSubtotal + cartTax : cartSubtotal;
  const checkoutTotal = Math.max(0, cartTotal - appliedCouponDiscount);
  const tenderedAmount = amountInput.trim() ? parseFloat(amountInput) : Number.NaN;
  const tenderedHasValue = Number.isFinite(tenderedAmount);
  const tenderedChange = tenderedHasValue ? Math.max(0, tenderedAmount - checkoutTotal) : 0;
  const tenderedShort = tenderedHasValue ? Math.max(0, checkoutTotal - tenderedAmount) : 0;

  useEffect(() => {
    if (!appliedCouponCode) return;
    setAppliedCouponCode(null);
    setAppliedCouponDiscount(0);
    setCouponStatus('idle');
    setCouponMessage(null);
  }, [cartTotal]);

  useEffect(() => {
    printerService.tryAutoReconnectSerial().then(() => {
      setPrinterStatus(printerService.getStatus());
    });
  }, []);

  const handlePrinterConnect = useCallback(() => {
    setShowPrinterDrawer((open) => !open);
  }, []);

  const createNewOrder = async () => {
    if (!currentOrder.id && cart.length === 0) return;

    setLoadingOrder(true);
    setCustomer(null);
    setPaymentMethod('cash');
    setPromoCodeInput('');
    setAppliedCouponCode(null);
    setAppliedCouponDiscount(0);
    setCouponStatus('idle');
    setCouponMessage(null);

    setActiveOrders(prev => {
      const nextOrders = [...prev, { id: null, localLines: [] } as any];
      setActiveIndex(nextOrders.length - 1);
      return nextOrders;
    });
    setLoadingOrder(false);
  };

  const addServiceChargeItem = () => {
    if (!currentOrder.id && activeOrders.length === 0) return;
    const serviceLine: CartLine = {
      product: {
        id: `service-${Date.now()}`,
        name: 'Service Charge',
        sku: 'SERVICE',
        barcode: null,
        base_price: '0',
        tax_rate: '0',
        is_active: true
      },
      qty: 1,
      unit_price: 100,
      discount_amount: 0,
      service_charge: 100
    };
    const newLines = [...cart, serviceLine];
    const updatedOrders = activeOrders.map((o, i) => i === activeIndex ? { ...o, localLines: newLines } : o);
    setActiveOrders(updatedOrders);
    setSelectedLineId(serviceLine.product.id);
    setKeypadMode('service');
    if (currentOrder.id) syncOrderToBackend(currentOrder.id, newLines);
  };

  useEffect(() => {
    if (activeOrders.length === 0 && !loadingOrder) {
      setActiveOrders([{ id: null, localLines: [] } as any]);
      setActiveIndex(0);
    }
    loadReceiptConfiguration(currentStoreId)
      .then(setReceiptConfig)
      .catch(() => {});

    accountingApi.listAccountsForCheckout(currentStoreId)
      .then((rows) => setPaymentAccounts(rows.map(mapAccountingAccount)))
      .catch(() => setPaymentAccounts([]));

    offersApi.listDiscountRules(currentStoreId)
      .then(rules => setAutoDiscounts(rules))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (paymentAccounts.length === 0) return;
    if (!paymentAccounts.some((account) => account.payment_method === paymentMethod)) {
      setPaymentMethod(paymentAccounts[0].payment_method || 'cash');
    }
  }, [paymentAccounts, paymentMethod]);

  const syncOrderToBackend = (orderId: string, lines: CartLine[]) => {
    if (!draftSyncEnabledRef.current) return;
    if (syncDebounceRef.current) clearTimeout(syncDebounceRef.current);
    syncDebounceRef.current = setTimeout(async () => {
      if (!draftSyncEnabledRef.current) return;
      try {
        await posApi.updateOrder(orderId, {
          storeId: currentStoreId,
          session_id: sessionId,
          customer_id: customer?.id || undefined,
          status: 'draft',
          promo_code: appliedCouponCode,
          notes: currentOrder?.notes || undefined,
          lines: lines.map(toOrderLineRequest)
        });
      } catch (e) {
        console.error('Failed to sync draft order', e);
      }
    }, 1000);
  };

  const buildCouponItems = (lines: CartLine[]) => lines
    .filter((line: CartLine) => !line.product.id.startsWith('service-'))
    .map((line: CartLine) => ({
      product_id: line.product.id,
      category_id: line.product.category ?? null,
      quantity: line.qty,
      unit_price: line.unit_price,
    }));

  const applyCouponCode = async (rawCode: string) => {
    const code = rawCode.trim();
    if (!code) {
      setAppliedCouponCode(null);
      setAppliedCouponDiscount(0);
      setCouponStatus('idle');
      setCouponMessage(null);
      return { valid: false, message: 'Enter a coupon code first.' };
    }

    const couponItems = buildCouponItems(cart);
    if (couponItems.length === 0) {
      setCouponStatus('invalid');
      setCouponMessage('Add at least one product before applying a coupon.');
      return { valid: false, message: 'Add at least one product before applying a coupon.' };
    }

    const couponCartTotal = couponItems.reduce(
      (sum, item) => sum + (item.unit_price * item.quantity),
      0,
    );

    setCouponStatus('validating');
    try {
      const validation = await offersApi.validateCoupon(currentStoreId, {
        coupon_code: code,
        cart_total: couponCartTotal,
        items: couponItems,
      });

      const discount = Number(validation.discount_amount || 0);
      if (!validation.valid) {
        setAppliedCouponCode(null);
        setAppliedCouponDiscount(0);
        setCouponStatus('invalid');
        setCouponMessage(validation.message);
        return { valid: false, message: validation.message };
      }

      setAppliedCouponCode(code);
      setAppliedCouponDiscount(discount);
      setCouponStatus('valid');
      setCouponMessage(
        discount > 0
          ? `${validation.message} Discount: KSh ${discount.toFixed(2)}`
          : validation.message,
      );
      return { valid: true, code, discount, message: validation.message };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to validate coupon.';
      setAppliedCouponCode(null);
      setAppliedCouponDiscount(0);
      setCouponStatus('invalid');
      setCouponMessage(message);
      return { valid: false, message };
    }
  };

  const addToCart = (product: ProductSearchResult) => {
    const existingIndex = cart.findIndex((l: any) => l.product.id === product.id);
    let newLines;
    if (existingIndex > -1) {
      newLines = cart.map((l: any, i: number) => i === existingIndex ? { ...l, qty: l.qty + 1 } : l);
    } else {
      const unitPrice = parseFloat(product.base_price);
      newLines = [...cart, { product, qty: 1, discount_amount: 0, unit_price: unitPrice, service_charge: 0 }];
    }

    setActiveOrders(prev => prev.map((o, i) => i === activeIndex ? { ...o, localLines: newLines } : o));
    setSelectedLineId(product.id);
    setSearch('');

    let orderId = currentOrder.id;
    if (!orderId) {
      if (creatingOrderPromiseRef.current) {
        creatingOrderPromiseRef.current.then((newId: string) => {
          syncOrderToBackend(newId, newLines);
        }).catch(() => {});
      } else {
        const createPromise = posApi.createOrder({
          storeId: currentStoreId,
          session_id: sessionId,
          status: 'draft',
          lines: newLines,
        }).then(newOrder => {
          setActiveOrders(prev =>
            prev.map((o, i) => i === activeIndex ? { ...o, id: newOrder.id } : o)
          );
          syncOrderToBackend(newOrder.id, newLines);
          return newOrder.id;
        }).catch(err => {
          console.error('Failed to create order', err);
          return null;
        }).finally(() => {
          creatingOrderPromiseRef.current = null;
        });
        creatingOrderPromiseRef.current = createPromise;
      }
    } else {
      syncOrderToBackend(orderId, newLines);
    }
  };

  const submitCharge = async () => {
    if (!currentOrder.id) {
      const created = await posApi.createOrder({ storeId: currentStoreId, session_id: sessionId, lines: cart });
      currentOrder.id = created.id;
    }
    if (paymentMethod === 'credit' && !customer) {
      alert('Please select a customer before using the Credit payment method.');
      return;
    }
    let couponCodeToSubmit = appliedCouponCode;
    if (promoCodeInput.trim()) {
      const validation = await applyCouponCode(promoCodeInput);
      if (!validation.valid) {
        alert(validation.message);
        return;
      }
      couponCodeToSubmit = validation.code ?? null;
    }

    if (cart.length === 0 && amountInput.trim() === '') {
      alert('Add at least one item before completing the order.');
      return;
    }

    const finalDueAmount = cart.length === 0 && amountInput.trim() ? parseFloat(amountInput) : checkoutTotal;
    const amountTendered = tenderedHasValue ? tenderedAmount : finalDueAmount;

    if (paymentMethod === 'cash' && amountTendered < finalDueAmount) {
      alert(`Amount tendered (KSh ${amountTendered.toFixed(2)}) is less than the total due (KSh ${finalDueAmount.toFixed(2)}).`);
      return;
    }

    const payloadAmount = paymentMethod === 'cash' ? amountTendered : finalDueAmount;

    setCharging(true);
    try {
      draftSyncEnabledRef.current = false;
      if (syncDebounceRef.current) {
        clearTimeout(syncDebounceRef.current);
        syncDebounceRef.current = null;
      }
      let payloadLines = cart.map(toOrderLineRequest);
      if (payloadLines.length === 0 && finalDueAmount > 0) {
        payloadLines = [{
          product_id: null,
          product_name: 'Custom Amount Sale',
          qty: 1,
          unit_price: finalDueAmount,
          discount_total: 0,
          service_charge: 0,
        }];
      }

      await posApi.updateOrder(currentOrder.id, {
        storeId: currentStoreId,
        session_id: sessionId,
        customer_id: customer?.id || undefined,
        status: 'completed',
        promo_code: couponCodeToSubmit,
        notes: currentOrder?.notes || undefined,
        lines: payloadLines,
        payments: [{
          method: paymentMethod,
          amount: payloadAmount,
          reference: paidTransactionDetails?.transactionId || paidTransactionDetails?.reference || stkReference || undefined,
        }],
      });

      setSuccess(true);
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Checkout failed');
    } finally {
      setCharging(false);
      setShowCashTenderModal(false);
    }
  };

  useEffect(() => {
    if (showPayment && currentStoreId) {
      setLoadingChannels(true);
      apiClient.get<any>(API_ENDPOINTS.MERCHANT.CHANNELS.LIST)
        .then(res => {
          const list = Array.isArray(res?.channels) ? res.channels : (Array.isArray(res) ? res : []);
          setPaymentChannels(list);
        })
        .catch(() => setPaymentChannels([]))
        .finally(() => setLoadingChannels(false));

      if (customer?.phone && !stkPhone) {
        setStkPhone(customer.phone);
      }
    }
  }, [showPayment, currentStoreId, customer?.phone]);

  useEffect(() => {
    if (!currentStoreId) return;

    const handleSseEvent = (e: Event) => {
      const customEvent = e as CustomEvent;
      const { type, data } = customEvent.detail;

      if (type === 'payment-completed') {
        if (data?.orderId === currentOrder.id || data?.reference === stkReference || (activeOrders && activeOrders.some(o => o.id === data?.orderId))) {
          setPaidTransactionDetails(data);
          setPaymentMethod('mobile_money');
          setStkStatus('success');
        }
      } else if (type === 'order-updated') {
        const statusUpper = data?.status?.toString().toUpperCase();
        const paymentStatusUpper = data?.paymentStatus?.toString().toUpperCase();
        if ((data?.orderId === currentOrder.id || data?.reference === stkReference) && (paymentStatusUpper === 'COMPLETED' || paymentStatusUpper === 'PAID' || statusUpper === 'CONFIRMED' || statusUpper === 'PROCESSING' || statusUpper === 'COMPLETED')) {
          setPaidTransactionDetails(data);
          setPaymentMethod('mobile_money');
          setStkStatus('success');
        }
      } else if (type === 'payment-failed') {
        if (data?.orderId === currentOrder.id || data?.reference === stkReference) {
          setStkMessage(data?.errorMessage || 'Payment failed on customer device.');
          setStkStatus('error');
        }
      }
    };

    window.addEventListener('merchant-sse-event', handleSseEvent);
    return () => window.removeEventListener('merchant-sse-event', handleSseEvent);
  }, [currentStoreId, currentOrder.id, stkReference, activeOrders]);

  useEffect(() => {
    if (stkStatus !== 'sent' || !stkReference) return;
    let cancelled = false;
    const interval = setInterval(async () => {
      try {
        const res: any = await apiClient.get(`/payments/status/${stkReference}`);
        const statusUpper = res?.status?.toString().toUpperCase();
        if (!cancelled && res && (statusUpper === 'CONFIRMED' || statusUpper === 'COMPLETED' || statusUpper === 'PAID' || statusUpper === 'SUCCESS' || res.status?.toString().toLowerCase() === 'success')) {
          setPaidTransactionDetails(res);
          setPaymentMethod('mobile_money');
          setStkStatus('success');
        } else if (!cancelled && res && (statusUpper === 'FAILED' || res.status?.toString().toLowerCase() === 'failed')) {
          setStkMessage(res.errorMessage || res.reason || 'Payment was cancelled or failed.');
          setStkStatus('error');
        }
      } catch {
        // ignore polling errors while waiting
      }
    }, 2500);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [stkStatus, stkReference]);

  const handleStkPushPrompt = async () => {
    if (!stkPhone || !stkPhone.trim()) {
      alert('Please enter a valid M-Pesa phone number (e.g. 0712345678 or 254712345678).');
      return;
    }

    const activeMpesaChannel = paymentChannels.find(
      (ch: any) => (ch.channelType === 'MPESA_PAYBILL' || ch.channelType === 'MPESA_TILL' || ch.provider === 'PAYHERO' || ch.provider === 'MPESA' || ch.type?.includes('MPESA') || ch.name?.toLowerCase().includes('mpesa')) &&
                   (ch.status === 'ACTIVE' || ch.isActive === true)
    ) || paymentChannels.find((ch: any) => ch.status === 'ACTIVE' || ch.isActive === true);

    const hasAnyActiveProvider = paymentChannels.some((ch: any) => ch.status === 'ACTIVE' || ch.isActive === true);

    if (!hasAnyActiveProvider || !activeMpesaChannel) {
      setStkStatus('error');
      const msg = 'No active payment provider receiving funds is configured. Please activate M-Pesa channel in Payment Settings before prompting.';
      setStkMessage(msg);
      alert(msg);
      return;
    }

    setStkSending(true);
    setStkStatus('idle');
    setStkMessage('');
    try {
      let activeOrderId = currentOrder.id;
      if (!activeOrderId || activeOrderId.startsWith('pos-')) {
        const persistedId = await posApi.persistDraftForPayment(activeOrderId, {
          storeId: currentStoreId,
          session_id: sessionId,
          customer_id: customer?.id,
          lines: cart,
          totalAmount: checkoutTotal
        });
        activeOrderId = persistedId;
        currentOrder.id = persistedId;
        setActiveOrders(prev => prev.map((o, i) => i === activeIndex ? { ...o, id: persistedId } : o));
      }

      const res = await apiClient.post<any>('/payments/initiate', {
        orderId: activeOrderId,
        phone: stkPhone.trim(),
        amount: checkoutTotal,
        channelId: activeMpesaChannel?.id || activeMpesaChannel?.channelId
      });

      setStkStatus('sent');
      setStkReference(res?.reference || res?.data?.reference || `STK-${Date.now().toString().slice(-6)}`);
      setStkMessage(res?.message || res?.data?.message || `STK prompt sent to ${stkPhone}`);
    } catch (err: any) {
      setStkStatus('error');
      const msg = err?.error || err?.message || 'Failed to initiate M-Pesa STK Push. Check payment provider configuration.';
      setStkMessage(msg);
    } finally {
      setStkSending(false);
    }
  };

  const handleCharge = async () => {
    if (paymentMethod === 'cash' && !amountInput.trim()) {
      setShowCashTenderModal(true);
      return;
    }
    await submitCharge();
  };

  const handleKeypadInput = (key: string) => {
    if (showPayment) {
      if (key === 'backspace') {
        setAmountInput(prev => prev.slice(0, -1));
      } else if (key === '+/-' || key === 'qty' || key === 'disc' || key === 'price') {
      } else {
        setAmountInput(prev => prev + key);
      }
      return;
    }

    const activeLineId = selectedLineId || (cart.length > 0 ? cart[cart.length - 1]?.product.id : null);
    if (!activeLineId) return;
    const newLines = cart.map((l: any) => {
      if (l.product.id !== activeLineId) return l;

      const updateValue = (currentVal: number, input: string) => {
        if (input === 'backspace') {
          isNewInputRef.current = false;
          const str = currentVal.toString();
          const next = str.slice(0, -1);
          return next ? parseFloat(next) : 0;
        }
        if (input === '+/-') return currentVal * -1;
        
        let currentStr = currentVal.toString();
        if (isNewInputRef.current) {
          currentStr = '';
          isNewInputRef.current = false;
        }
        
        if (currentStr === '0' && input !== '.') currentStr = '';
        const nextStr = currentStr + input;
        const nextVal = parseFloat(nextStr);
        return isNaN(nextVal) ? currentVal : nextVal;
      };

      if (keypadMode === 'qty') return { ...l, qty: updateValue(l.qty, key) };
      if (keypadMode === 'disc') {
        const currentPct = l.unit_price > 0 ? Math.round((l.discount_amount / l.unit_price) * 100) : 0;
        const newPct = Math.min(Math.max(updateValue(currentPct, key), 0), 100);
        return { ...l, discount_amount: l.unit_price * (newPct / 100) };
      }
      if (keypadMode === 'price') return { ...l, unit_price: updateValue(l.unit_price, key) };
      if (keypadMode === 'service') return { ...l, service_charge: updateValue(l.service_charge || 0, key) };
      return l;
    });

    setActiveOrders(prev => prev.map((o, i) => i === activeIndex ? { ...o, localLines: newLines } : o));
    if (currentOrder.id) syncOrderToBackend(currentOrder.id, newLines);
  };

  const removeLineFromCart = (productId: string) => {
    const newLines = cart.filter((l: any) => l.product.id !== productId);
    setActiveOrders(prev => prev.map((o, i) => i === activeIndex ? { ...o, localLines: newLines } : o));
    if (currentOrder.id) syncOrderToBackend(currentOrder.id, newLines);
    if (selectedLineId === productId) setSelectedLineId(null);
  };

  const clearCart = async () => {
    const confirmed = await confirmAction({
      title: 'Void & Clear Order',
      message: 'Are you sure you want to void and clear this order?',
      confirmText: 'Void Order',
      cancelText: 'Keep Order',
    });
    if (confirmed) {
      if (currentOrder.id) {
        try {
          await posApi.voidOrder(currentOrder.id);
        } catch (e) {
          console.error('Failed to void order', e);
        }
      }
      
      const remainingOrders = activeOrders.filter((_, i) => i !== activeIndex);
      setActiveOrders(remainingOrders);
      setSelectedLineId(null);
      
      if (remainingOrders.length > 0) {
        setActiveIndex(Math.min(activeIndex, remainingOrders.length - 1));
      } else {
        createNewOrder();
      }
    }
  };

  const handleKeypadInputRef = useRef(handleKeypadInput);
  handleKeypadInputRef.current = handleKeypadInput;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName || '')) return;
      const key = e.key;
      if (/^[0-9]$/.test(key) || key === '.') {
        handleKeypadInputRef.current(key);
      } else if (key === 'Backspace') {
        handleKeypadInputRef.current('backspace');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const cashierDisplayName = (user as any)?.name || `${(user as any)?.firstName || ''} ${(user as any)?.lastName || ''}`.trim() || 'Cashier Staff';

  const registerContent = (
    <div className="fixed inset-0 z-50 bg-[#F6F6F7] text-[#202223] flex flex-col font-sans overflow-hidden select-none">
      {/* HEADER - CLEAN LIGHT THEME */}
      <header className="h-16 !bg-white !text-[#202223] border-b border-[#E1E3E5] px-5 flex items-center justify-between shrink-0 shadow-xs z-10">
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-xl border border-slate-200 bg-slate-50 hover:bg-red-50 text-slate-600 hover:text-red-600 hover:border-red-200 transition-all duration-200 shadow-xs flex items-center justify-center"
            title="Exit Register"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2.5">
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[#006838] text-white rounded-xl font-black text-xs tracking-wider shadow-xs">
              <span>DUKENE POS</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="relative w-72 hidden sm:block">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search products or scan barcode..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full h-9 pl-9 pr-3 text-xs bg-[#F8F9FA] border border-[#D2D5D9] hover:border-[#BABFC5] rounded-xl text-[#202223] placeholder:text-slate-400 focus:outline-none focus:bg-white focus:border-[#006838] focus:ring-2 focus:ring-[#006838]/10 transition-all shadow-inner"
            />
          </div>

          <button
            type="button"
            onClick={handlePrinterConnect}
            className={`flex items-center gap-2 px-3.5 py-1.5 rounded-xl text-xs font-bold border shadow-xs transition-all ${
              printerStatus.connected 
                ? 'bg-emerald-50 border-emerald-200 text-[#006838] hover:bg-emerald-100/80' 
                : 'bg-white border-[#D2D5D9] text-slate-700 hover:bg-slate-50'
            }`}
          >
            <Printer className={`w-4 h-4 ${printerStatus.connected ? 'text-[#006838]' : 'text-slate-500'}`} />
            <span>{printerStatus.connected ? 'Printer Connected' : 'Connect Printer'}</span>
          </button>
        </div>
      </header>

      {/* BODY */}
      <div className="flex flex-1 overflow-hidden">
        {/* LEFT / CENTER PANEL: PRODUCT GRID OR PAYMENT SCREEN */}
        <div className="flex-1 flex flex-col bg-slate-100 dark:bg-slate-900 overflow-hidden">
          {showPayment ? (
            <div className="flex-1 flex flex-col p-6 overflow-y-auto max-w-2xl mx-auto w-full space-y-6">
              <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-4">
                <div>
                  <h2 className="text-lg font-bold text-slate-900 dark:text-white">Payment & Checkout</h2>
                  <p className="text-xs text-slate-500">Select payment method and confirm tendered amount</p>
                </div>
                <div className="text-right">
                  <p className="text-xs font-semibold text-slate-500">Total Due</p>
                  <p className="text-2xl font-extrabold text-emerald-600 dark:text-emerald-400">
                    KES {checkoutTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </p>
                </div>
              </div>

              {stkStatus === 'success' && paymentMethod !== 'mobile_money' && (
                <div className="bg-emerald-100/90 dark:bg-emerald-900/80 border-2 border-emerald-500 p-4 rounded-2xl flex items-center justify-between gap-4 shadow-md animate-in fade-in zoom-in-95 duration-200">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="w-6 h-6 text-emerald-600 dark:text-emerald-400 shrink-0" />
                    <div>
                      <p className="text-xs font-black text-emerald-900 dark:text-emerald-100">M-Pesa / Mobile Payment Received!</p>
                      <p className="text-[11px] text-emerald-800 dark:text-emerald-200">
                        Paid by <strong className="font-bold">{paidTransactionDetails?.customerName || paidTransactionDetails?.payerName || paidTransactionDetails?.name || customer?.name || 'M-Pesa Customer'}</strong>
                        {(paidTransactionDetails?.phone || paidTransactionDetails?.customerPhone || stkPhone) && ` (${paidTransactionDetails?.phone || paidTransactionDetails?.customerPhone || stkPhone})`}
                        {' • '}KES {(paidTransactionDetails?.amount ? Number(paidTransactionDetails.amount) : checkoutTotal).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('mobile_money')}
                    className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold shadow shrink-0"
                  >
                    View & Complete
                  </button>
                </div>
              )}

              {/* PAYMENT METHOD SELECTOR */}
              <div className="grid grid-cols-4 gap-3">
                {[
                  { id: 'cash', label: 'Cash', icon: Banknote, color: 'emerald' },
                  { id: 'mobile_money', label: 'M-Pesa', icon: Smartphone, color: 'emerald' },
                  { id: 'credit', label: 'Customer Credit', icon: FileText, color: 'blue' },
                  { id: 'bank', label: 'Card / Bank', icon: CreditCard, color: 'purple' },
                ].map((m) => {
                  const Icon = m.icon;
                  const active = paymentMethod === m.id;
                  return (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => setPaymentMethod(m.id)}
                      className={`p-3.5 rounded-xl border flex flex-col items-center justify-center gap-2 transition-all ${
                        active
                          ? 'bg-emerald-600 border-emerald-600 text-white shadow-md'
                          : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="text-xs font-bold">{m.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* CASH SPECIFIC INPUTS */}
              {paymentMethod === 'cash' && (
                <div className="bg-white dark:bg-slate-800 p-5 rounded-xl border border-slate-200 dark:border-slate-700 space-y-4 shadow-xs">
                  <div className="space-y-1.5">
                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
                      Amount Tendered by Customer (KES)
                    </label>
                    <input
                      type="number"
                      placeholder={`Exact due: ${checkoutTotal}`}
                      value={amountInput}
                      onChange={(e) => setAmountInput(e.target.value)}
                      className="w-full h-11 px-3 text-lg font-bold bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white focus:outline-none focus:border-emerald-500"
                    />
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {[checkoutTotal, Math.ceil(checkoutTotal / 100) * 100, 500, 1000, 2000, 5000].map((amt, idx) => {
                      if (amt < checkoutTotal && idx > 0) return null;
                      return (
                        <button
                          key={`${amt}-${idx}`}
                          type="button"
                          onClick={() => setAmountInput(amt.toString())}
                          className="px-3.5 py-1.5 bg-slate-100 dark:bg-slate-700 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 rounded-lg text-xs font-bold text-slate-800 dark:text-slate-200 transition-colors"
                        >
                          +{amt.toLocaleString()}
                        </button>
                      );
                    })}
                  </div>

                  {tenderedHasValue && (
                    <div className="flex items-center justify-between pt-3 border-t border-slate-200 dark:border-slate-700">
                      <span className="text-xs font-bold text-slate-600 dark:text-slate-400">
                        {tenderedChange > 0 ? 'Change to Return:' : tenderedShort > 0 ? 'Balance Short:' : 'Exact Amount'}
                      </span>
                      <span className={`text-lg font-extrabold ${tenderedChange > 0 ? 'text-emerald-600 dark:text-emerald-400' : tenderedShort > 0 ? 'text-red-600' : 'text-slate-900 dark:text-white'}`}>
                        KES {tenderedChange > 0 ? tenderedChange.toFixed(2) : tenderedShort > 0 ? tenderedShort.toFixed(2) : '0.00'}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* MPESA SPECIFIC INFO & STK PUSH PROMPT */}
              {paymentMethod === 'mobile_money' && (
                <div className="bg-white dark:bg-slate-800 p-5 rounded-xl border border-slate-200 dark:border-slate-700 space-y-4 shadow-sm">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-bold text-slate-900 dark:text-white flex items-center gap-2">
                      <Smartphone className="w-4 h-4 text-emerald-600" />
                      <span>M-Pesa Express (STK Push) & Paybill</span>
                    </h4>
                    {loadingChannels ? (
                      <span className="text-[11px] text-slate-400 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Checking provider...</span>
                    ) : (() => {
                      const hasActiveProvider = paymentChannels.some((ch: any) => ch.status === 'ACTIVE' || ch.isActive === true);
                      return hasActiveProvider ? (
                        <span className="px-2 py-0.5 bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 rounded text-[11px] font-bold flex items-center gap-1 border border-emerald-200 dark:border-emerald-800">
                          <CheckCircle2 className="w-3 h-3 text-emerald-600" /> Provider Active
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 bg-red-100 dark:bg-red-950/60 text-red-700 dark:text-red-300 rounded text-[11px] font-bold flex items-center gap-1 border border-red-200 dark:border-red-800">
                          <AlertCircle className="w-3 h-3 text-red-600" /> No Active Provider
                        </span>
                      );
                    })()}
                  </div>

                  {stkStatus === 'idle' && (
                    <div className="space-y-3 bg-emerald-50/50 dark:bg-slate-900/50 p-4 rounded-xl border border-emerald-200/60 dark:border-slate-700">
                      {(() => {
                        const hasActiveProvider = paymentChannels.some((ch: any) => ch.status === 'ACTIVE' || ch.isActive === true);
                        if (!hasActiveProvider && !loadingChannels) {
                          return (
                            <div className="p-3 bg-amber-50 dark:bg-amber-950/50 border border-amber-200 dark:border-amber-800 rounded-xl text-amber-800 dark:text-amber-200 text-xs flex items-start gap-2 mb-2">
                              <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                              <div>
                                <p className="font-bold">Active Payment Provider Required</p>
                                <p className="text-[11px] mt-0.5 text-amber-700 dark:text-amber-300">An active M-Pesa payment channel receiving funds is required before triggering customer prompts. Activate a channel in Payment Settings.</p>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })()}
                      <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
                        Customer M-Pesa Phone Number
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="tel"
                          placeholder="e.g. 0712 345 678 or 254712345678"
                          value={stkPhone}
                          onChange={(e) => setStkPhone(e.target.value)}
                          disabled={stkSending || loadingChannels || !paymentChannels.some((ch: any) => ch.status === 'ACTIVE' || ch.isActive === true)}
                          className="flex-1 h-10 px-3.5 text-xs font-medium bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-xl text-slate-900 dark:text-white disabled:opacity-50 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/10"
                        />
                        <button
                          type="button"
                          onClick={handleStkPushPrompt}
                          disabled={stkSending || loadingChannels || !paymentChannels.some((ch: any) => ch.status === 'ACTIVE' || ch.isActive === true)}
                          className="h-10 px-4 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-bold text-xs rounded-xl flex items-center gap-1.5 shadow transition-all"
                        >
                          {stkSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                          <span>Send Prompt</span>
                        </button>
                      </div>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400">
                        When clicked, an M-Pesa PIN prompt for <strong className="font-bold text-slate-800 dark:text-slate-200">KES {checkoutTotal.toLocaleString()}</strong> is sent directly to their phone.
                      </p>
                    </div>
                  )}

                  {stkStatus === 'sent' && (
                    <div className="bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-300 dark:border-emerald-800 p-4 rounded-xl space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-emerald-800 dark:text-emerald-300 font-bold text-xs">
                          <Loader2 className="w-4 h-4 animate-spin text-emerald-600" />
                          <span>STK Push Prompt Sent to {stkPhone}</span>
                        </div>
                        <span className="text-[11px] font-mono text-emerald-700 bg-emerald-100 dark:bg-emerald-900/60 px-2 py-0.5 rounded">
                          {stkReference}
                        </span>
                      </div>
                      <p className="text-xs text-slate-700 dark:text-slate-300">
                        Ask the customer to enter their M-Pesa PIN on their phone to complete the payment of <strong className="font-bold">KES {checkoutTotal.toLocaleString()}</strong>.
                      </p>
                      {stkMessage && <p className="text-[11px] text-emerald-600 font-medium italic">"{stkMessage}"</p>}
                      <div className="flex items-center justify-end gap-2 pt-2 border-t border-emerald-200/60 dark:border-emerald-800/60">
                        <button
                          type="button"
                          onClick={() => setStkStatus('idle')}
                          className="px-3 py-1.5 bg-white dark:bg-slate-800 hover:bg-slate-100 text-slate-700 dark:text-slate-300 rounded-lg text-xs font-semibold border border-slate-300 dark:border-slate-700"
                        >
                          Change Phone / Retry
                        </button>
                        <button
                          type="button"
                          onClick={handleCharge}
                          disabled={charging}
                          className="px-3.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold shadow flex items-center gap-1.5"
                        >
                          <CheckCircle2 className="w-4 h-4" />
                          <span>Complete Sale Now</span>
                        </button>
                      </div>
                    </div>
                  )}

                  {stkStatus === 'success' && (
                    <div className="bg-emerald-100/80 dark:bg-emerald-900/60 border-2 border-emerald-500 dark:border-emerald-600 p-5 rounded-2xl space-y-4 shadow-sm animate-in fade-in zoom-in-95 duration-200">
                      <div className="flex items-center justify-between border-b border-emerald-300/60 dark:border-emerald-800/60 pb-3">
                        <div className="flex items-center gap-2.5 text-emerald-900 dark:text-emerald-200 font-black text-sm">
                          <CheckCircle2 className="w-6 h-6 text-emerald-600 dark:text-emerald-400 shrink-0" />
                          <span>M-Pesa Payment Confirmed!</span>
                        </div>
                        <span className="text-xs font-mono font-bold text-emerald-800 dark:text-emerald-200 bg-emerald-200 dark:bg-emerald-800/80 px-2.5 py-1 rounded-lg">
                          {paidTransactionDetails?.transactionId || paidTransactionDetails?.providerRef || stkReference}
                        </span>
                      </div>
                      
                      <div className="bg-white/80 dark:bg-slate-900/80 rounded-xl p-3.5 space-y-2.5 text-xs border border-emerald-200 dark:border-emerald-800/50">
                        <div className="flex justify-between items-center text-slate-600 dark:text-slate-400">
                          <span>Paid By:</span>
                          <span className="font-bold text-slate-900 dark:text-white flex items-center gap-1.5">
                            <span>{paidTransactionDetails?.customerName || paidTransactionDetails?.payerName || paidTransactionDetails?.name || customer?.name || 'M-Pesa Customer'}</span>
                            {(paidTransactionDetails?.phone || paidTransactionDetails?.customerPhone || stkPhone || customer?.phone) && (
                              <span className="font-mono text-xs text-emerald-700 dark:text-emerald-300 bg-emerald-200/60 dark:bg-emerald-900/40 px-1.5 py-0.5 rounded">
                                {paidTransactionDetails?.phone || paidTransactionDetails?.customerPhone || stkPhone || customer?.phone}
                              </span>
                            )}
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-slate-600 dark:text-slate-400">
                          <span>Amount Received:</span>
                          <span className="font-black text-emerald-600 dark:text-emerald-400 text-sm">
                            KES {(paidTransactionDetails?.amount ? Number(paidTransactionDetails.amount) : checkoutTotal).toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-slate-600 dark:text-slate-400">
                          <span>Status:</span>
                          <span className="font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wide">
                            Success / Paid
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center justify-end gap-2.5 pt-1">
                        <button
                          type="button"
                          onClick={() => {
                            setStkStatus('idle');
                            setPaidTransactionDetails(null);
                          }}
                          className="px-3.5 py-2 bg-white dark:bg-slate-800 hover:bg-slate-100 text-slate-700 dark:text-slate-300 rounded-xl text-xs font-semibold border border-slate-300 dark:border-slate-700 transition-colors"
                        >
                          Close Prompt
                        </button>
                        <button
                          type="button"
                          onClick={handleCharge}
                          disabled={charging}
                          className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold shadow-md hover:shadow-lg flex items-center gap-1.5 transition-all"
                        >
                          <CheckCircle2 className="w-4 h-4" />
                          <span>Complete & Print Receipt</span>
                        </button>
                      </div>
                    </div>
                  )}

                  {stkStatus === 'error' && (
                    <div className="bg-red-50 dark:bg-red-950/40 border border-red-300 dark:border-red-800 p-4 rounded-xl space-y-2">
                      <div className="flex items-center gap-2 text-red-800 dark:text-red-300 font-bold text-xs">
                        <AlertCircle className="w-4 h-4 text-red-600" />
                        <span>Could Not Send STK Prompt</span>
                      </div>
                      <p className="text-xs text-red-700 dark:text-red-300">
                        {stkMessage || 'Failed to trigger M-Pesa prompt. Please verify that your M-Pesa payment provider channel is active.'}
                      </p>
                      <div className="flex items-center justify-end gap-2 pt-1">
                        <button
                          type="button"
                          onClick={() => setStkStatus('idle')}
                          className="px-3 py-1 bg-white dark:bg-slate-800 hover:bg-slate-100 text-slate-700 dark:text-slate-300 rounded-lg text-xs font-semibold border border-slate-300 dark:border-slate-700"
                        >
                          Try Again
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="pt-2 border-t border-slate-100 dark:border-slate-700/60">
                    <p className="text-[11px] text-slate-400">
                      Or confirm manual receipt via M-Pesa SMS / Till notification before clicking Confirm Sale below.
                    </p>
                  </div>
                </div>
              )}

              {/* COUPON INPUT */}
              <div className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-slate-200 dark:border-slate-700 flex items-center gap-2">
                <input
                  type="text"
                  placeholder="Promo or discount coupon code..."
                  value={promoCodeInput}
                  onChange={(e) => setPromoCodeInput(e.target.value)}
                  className="flex-1 h-9 px-3 text-xs bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => applyCouponCode(promoCodeInput)}
                  className="px-4 h-9 bg-slate-900 dark:bg-slate-700 hover:bg-slate-800 text-white font-bold text-xs rounded-lg"
                >
                  Apply
                </button>
              </div>

              {couponMessage && (
                <p className={`text-xs font-semibold ${couponStatus === 'valid' ? 'text-emerald-600' : 'text-red-600'}`}>
                  {couponMessage}
                </p>
              )}

              {/* BOTTOM ACTIONS */}
              <div className="flex items-center gap-3 pt-4 border-t border-slate-200 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => setShowPayment(false)}
                  className="flex-1 h-11 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 hover:bg-slate-50 rounded-xl text-xs font-bold text-slate-700 dark:text-slate-300"
                >
                  Back to Cart
                </button>
                <button
                  type="button"
                  onClick={handleCharge}
                  disabled={charging}
                  className="flex-2 h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold text-sm rounded-xl inline-flex items-center justify-center gap-2 shadow-lg"
                >
                  {charging && <Loader2 className="w-4 h-4 animate-spin" />}
                  <span>Confirm Sale (KES {checkoutTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })})</span>
                </button>
              </div>
            </div>
          ) : (
            <ProductGrid onAdd={addToCart} autoDiscounts={autoDiscounts} searchQuery={search} storeId={currentStoreId} />
          )}
        </div>

        {/* RIGHT PANEL: CART ITEMS, KEYPAD & TOTALS */}
        <div className="w-96 bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-800 flex flex-col justify-between overflow-hidden shrink-0">
          {/* CUSTOMER HEADER */}
          <div className="p-3 bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center text-emerald-600 dark:text-emerald-400 font-bold text-xs">
                {customer ? customer.name.charAt(0) : <User className="w-4 h-4" />}
              </div>
              <div>
                <p className="text-xs font-bold text-slate-900 dark:text-white truncate max-w-[170px]">
                  {customer ? customer.name : 'Walk-In Customer'}
                </p>
                {customer?.phone && <p className="text-[10px] text-slate-500">{customer.phone}</p>}
              </div>
            </div>
            <div className="flex items-center gap-1">
              {customer && (
                <button
                  type="button"
                  onClick={() => setCustomer(null)}
                  className="p-1.5 text-slate-400 hover:text-red-600 rounded"
                  title="Remove customer"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowCustomerSearch(true)}
                className="px-2.5 py-1 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg text-xs font-semibold text-slate-700 dark:text-slate-300"
              >
                {customer ? 'Change' : '+ Assign'}
              </button>
            </div>
          </div>

          {/* CART ITEMS LIST */}
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {cart.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center p-6 text-slate-400 space-y-2">
                <FileText className="w-10 h-10 stroke-1 text-slate-300 dark:text-slate-700" />
                <p className="text-xs font-semibold text-slate-600 dark:text-slate-400">Cart is currently empty</p>
                <p className="text-[11px] text-slate-400">Click or scan items on the left to add to order</p>
              </div>
            ) : (
              cart.map((l: CartLine) => {
                const isSelected = selectedLineId === l.product.id;
                return (
                  <div
                    key={l.product.id}
                    onClick={() => setSelectedLineId(l.product.id)}
                    className={`p-2.5 rounded-xl border transition-all cursor-pointer flex items-center justify-between gap-2 ${
                      isSelected
                        ? 'bg-emerald-50/80 dark:bg-emerald-950/40 border-emerald-500 shadow-xs'
                        : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-slate-900 dark:text-white truncate">{l.product.name}</p>
                      <div className="flex items-center gap-2 mt-1 text-[11px] text-slate-500">
                        <span>{l.qty} × KES {l.unit_price.toLocaleString()}</span>
                        {l.discount_amount > 0 && (
                          <span className="text-red-600 font-semibold">(-KES {(l.discount_amount * l.qty).toLocaleString()})</span>
                        )}
                      </div>
                    </div>

                    <div className="text-right flex items-center gap-2 shrink-0">
                      <span className="text-xs font-extrabold text-slate-900 dark:text-white">
                        KES {(l.qty * (l.unit_price - l.discount_amount)).toLocaleString()}
                      </span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeLineFromCart(l.product.id);
                        }}
                        className="p-1.5 text-slate-400 hover:text-red-600 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* KEYPAD ALWAYS VISIBLE ON RIGHT PANEL WHEN NOT IN PAYMENT MODE */}
          {!showPayment && (
            <div className="shrink-0">
              <Keypad
                onInput={handleKeypadInput}
                mode={keypadMode}
                setMode={setKeypadMode}
                canApplyDiscount={canApplyDiscount}
                canOverridePrice={canOverridePrice}
              />
            </div>
          )}

          {/* BOTTOM TOTALS & CHECKOUT BUTTON */}
          <div className="p-4 bg-slate-50 dark:bg-slate-800/80 border-t border-slate-200 dark:border-slate-800 space-y-3 shrink-0">
            <div className="space-y-1.5 text-xs">
              <div className="flex justify-between text-slate-600 dark:text-slate-400">
                <span>Subtotal</span>
                <span>KES {cartSubtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
              {vatMode !== 'disabled' && (
                <div className="flex justify-between text-slate-600 dark:text-slate-400">
                  <span>Tax ({vatRate}% {vatMode})</span>
                  <span>KES {cartTax.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
              )}
              {appliedCouponDiscount > 0 && (
                <div className="flex justify-between text-red-600 font-semibold">
                  <span>Coupon Discount</span>
                  <span>- KES {appliedCouponDiscount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
              )}
              <div className="flex justify-between text-sm font-extrabold text-slate-900 dark:text-white pt-1 border-t border-slate-200 dark:border-slate-700">
                <span>Total Due</span>
                <span className="text-emerald-600 dark:text-emerald-400">
                  KES {checkoutTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 pt-1">
              <button
                type="button"
                onClick={addServiceChargeItem}
                className="h-8 rounded-lg bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-xs font-semibold text-slate-700 dark:text-slate-300 hover:bg-slate-100"
              >
                + Service
              </button>
              <button
                type="button"
                onClick={() => setShowNotesModal(true)}
                className="h-8 rounded-lg bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-xs font-semibold text-slate-700 dark:text-slate-300 hover:bg-slate-100"
              >
                Notes
              </button>
              <button
                type="button"
                onClick={clearCart}
                disabled={cart.length === 0}
                className="h-8 rounded-lg bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50"
              >
                Void
              </button>
            </div>

            <button
              type="button"
              onClick={() => {
                if (cart.length === 0) {
                  alert('Add items to cart before checkout.');
                  return;
                }
                setShowPayment(true);
              }}
              disabled={cart.length === 0}
              className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-extrabold text-sm rounded-xl shadow-lg transition-all flex items-center justify-center gap-2"
            >
              <span>Charge / Checkout</span>
              <span className="bg-emerald-800 px-2.5 py-0.5 rounded-md text-xs">
                KES {checkoutTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* MODALS */}
      {showCustomerSearch && (
        <CustomerSearch
          onSelect={(c) => setCustomer(c)}
          onClose={() => setShowCustomerSearch(false)}
        />
      )}

      {showPrinterDrawer && (
        <PrinterDrawer
          isOpen={showPrinterDrawer}
          onClose={() => setShowPrinterDrawer(false)}
        />
      )}

      {showNotesModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl w-full max-w-md p-5 space-y-4 shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-3">
              <h3 className="font-bold text-slate-900 dark:text-white text-sm">Order Notes</h3>
              <button
                type="button"
                onClick={() => setShowNotesModal(false)}
                className="p-1 rounded-lg text-slate-400 hover:text-slate-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <textarea
              rows={4}
              placeholder="Add kitchen notes, delivery instructions or cashier comments..."
              value={orderNotesInput}
              onChange={(e) => setOrderNotesInput(e.target.value)}
              className="w-full p-3 text-xs bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg focus:outline-none focus:border-emerald-500 text-slate-900 dark:text-white"
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowNotesModal(false)}
                className="px-4 py-1.5 rounded-lg bg-emerald-600 text-white font-bold text-xs"
              >
                Save Notes
              </button>
            </div>
          </div>
        </div>
      )}

      {showCashTenderModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl w-full max-w-sm p-5 space-y-4 shadow-xl text-center">
            <h3 className="font-bold text-slate-900 dark:text-white text-sm">Enter Cash Tendered</h3>
            <p className="text-xs text-slate-500">Total Due: KES {checkoutTotal.toLocaleString()}</p>
            <input
              type="number"
              placeholder="e.g. 1000"
              value={amountInput}
              onChange={(e) => setAmountInput(e.target.value)}
              className="w-full h-11 px-3 text-center text-lg font-bold bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white"
              autoFocus
            />
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setShowCashTenderModal(false)}
                className="flex-1 py-2 rounded-lg bg-slate-200 dark:bg-slate-800 text-xs font-bold"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitCharge}
                className="flex-1 py-2 rounded-lg bg-emerald-600 text-white text-xs font-bold"
              >
                Confirm Cash Sale
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SUCCESS RECEIPT MODAL */}
      {success && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-xs p-4 overflow-y-auto">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-md p-6 space-y-5 shadow-2xl my-auto">
            <div className="text-center space-y-2">
              <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center text-emerald-600 dark:text-emerald-400 mx-auto">
                <CheckCircle2 className="w-7 h-7" />
              </div>
              <h3 className="font-bold text-slate-900 dark:text-white text-lg">Sale Completed!</h3>
              <p className="text-xs text-slate-500">Receipt #{currentOrder.id || 'POS-SALE'}</p>
            </div>

            <div className="max-h-80 overflow-y-auto border border-slate-200 dark:border-slate-800 rounded-xl p-2 bg-slate-50 dark:bg-slate-950">
              <ReceiptPreview
                cart={cart}
                subtotal={cartSubtotal}
                tax={cartTax}
                total={checkoutTotal}
                config={receiptConfig}
                customer={customer}
                paymentMethod={paymentMethod}
                amountInput={amountInput}
                cashierName={cashierDisplayName}
                orderId={currentOrder.id}
                store={activeStoreObj}
                location={activeStoreObj}
                sessionId={sessionId}
              />
            </div>

            <div className="flex items-center gap-3 pt-2">
              <button
                type="button"
                onClick={() => {
                  const data: ReceiptData = {
                    cart,
                    subtotal: cartSubtotal,
                    tax: cartTax,
                    total: checkoutTotal,
                    config: receiptConfig,
                    customer,
                    paymentMethod,
                    amountInput,
                    cashierName: cashierDisplayName,
                    orderId: currentOrder.id,
                    store: activeStoreObj,
                    location: activeStoreObj,
                    sessionId,
                  };
                  printReceipt(data, printerService);
                }}
                className="flex-1 h-11 bg-slate-900 hover:bg-slate-800 dark:bg-slate-700 dark:hover:bg-slate-600 text-white font-bold text-xs rounded-xl inline-flex items-center justify-center gap-2 transition-colors"
              >
                <Printer className="w-4 h-4" />
                <span>Print Receipt</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  setSuccess(false);
                  setShowPayment(false);
                  setAmountInput('');
                  setCustomer(null);
                  setPromoCodeInput('');
                  setAppliedCouponCode(null);
                  setAppliedCouponDiscount(0);
                  const remaining = activeOrders.filter((_, i) => i !== activeIndex);
                  if (remaining.length > 0) {
                    setActiveOrders(remaining);
                    setActiveIndex(Math.min(activeIndex, remaining.length - 1));
                  } else {
                    setActiveOrders([{ id: null, localLines: [] } as any]);
                    setActiveIndex(0);
                  }
                }}
                className="flex-1 h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl transition-colors"
              >
                New Sale
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return createPortal(registerContent, document.body);
}
